import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, Renderer2/*, OnChanges, SimpleChanges*//*, AfterViewChecked*/ } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Moment } from 'moment';
import * as moment from 'moment';
import * as fileSaver from 'file-saver';

import { SlickGridComponent } from 'app/slick-grid/slick-grid.component';
import { BufferGraphService } from 'app/buffer-graph/services/buffer-graph.service';
import { ISlickGridColumn } from 'app/slick-grid/interfaces/slick-grid-column';
import { Formatters } from 'app/slick-grid/models/formatters';
import { EventArgs } from 'app/slick-grid/models/event-args';
import { TabComponentBase } from 'app/buffer-graph/models/tab-component-base';
import { ItemAction } from 'app/buffer-graph/models/item-actions/item-action';
import { FreshItemActionHeader } from 'app/buffer-graph/models/item-actions/fresh-item-action-header';
import { FreshItemActionLine } from 'app/buffer-graph/models/item-actions/fresh-item-action-line';
import { ResizeDirection } from 'app/resize-glyph/resize-glyph.component';
import { Size } from 'app/shared/models/size';

type Action = ItemAction | FreshItemActionHeader;

@Component({
    selector: 'item-actions',
    templateUrl: './item-actions.component.html',
    styleUrls: ['./item-actions.component.css']
})
export class ItemActionsComponent extends TabComponentBase<Action, { columns: { header: string, field: string }[], rows: string[][] }> /*implements OnChanges*//*, AfterViewChecked*/ {
    constructor(graphService: BufferGraphService, private readonly translateService: TranslateService, private readonly renderer: Renderer2, private readonly elementRef: ElementRef) {
        super(graphService);

        this.showCurrentActions = new EventEmitter<void>();
        this.showAllActions = new EventEmitter<void>();

        this.dataView = new Slick.Data.DataView<Action>();
        this.options = { syncColumnCellResize: true, multiColumnSort: true, editable: false };

        this.freshLineDataView = new Slick.Data.DataView<FreshItemActionLine>();
        this.freshLineOptions = { syncColumnCellResize: true, multiColumnSort: true, editable: false };

        this.resizeValue = 0;
    }

    @ViewChild('grid')
    protected gridRef: SlickGridComponent;

    @ViewChild('freshLinesGrid')
    private freshLinesGridRef: SlickGridComponent;

    @ViewChild('headers')
    private headersRef: ElementRef;

    @ViewChild('lines')
    private linesRef: ElementRef;

    @Input()
    protected isActive: boolean;

    @Input()
    private allShown: boolean;

    private data: Action[];
    @Input()
    private set actions(actions: Action[]) {
        this.data = actions;

        this.dataView.setItems(actions);
        this.selectedFreshHeader = null;
        this.freshLineDataView.setItems([]);
    }

    @Input()
    protected sku: string;

    @Input()
    protected warehouseCode: string;

    private isFresh: boolean;
    @Input()
    private set fresh(value: boolean) {
        if (this.isFresh === value && this.isFresh != null)
            return;

        this.isFresh = value;
        this.setColumns();
    }

    @Input()
    protected canSend: boolean;

    @Output()
    private readonly showCurrentActions: EventEmitter<void>;

    @Output()
    private readonly showAllActions: EventEmitter<void>;

    private get verticalDirection(): ResizeDirection {
        return ResizeDirection.Vertical;
    }

    private readonly freshLineDataView: Slick.Data.DataView<FreshItemActionLine>;

    private freshLineColumns: ISlickGridColumn<FreshItemActionLine>[];

    private readonly freshLineOptions: Slick.GridOptions<FreshItemActionLine>;

    private selectedFreshHeader: FreshItemActionHeader;

    private resizeTimer: any;

    private resizeValue: number;

    private freshTemplateRows: string;

    //ngOnChanges(changes: SimpleChanges): void {
    //    const freshChanges = changes['isFresh'];
    //    if (freshChanges != null)
    //        this.setColumns();
    //}

    //ngOnInit(): void {
    //    super.ngOnInit();

    //    this.setColumns();
    //}

    //ngAfterViewChecked(): void {
    //    //if (this.columns == null || this.isFresh == null)
    //    //    return;

    //    //setTimeout(() => {
    //    //},
    //    //    1);
    //}

    protected getExportData = this.getExportDataByDefault;

    protected onTabsResized(): boolean {
        if (!super.onTabsResized())
            return false;

        if (this.freshLinesGridRef)
            this.freshLinesGridRef.resizeGrid();

        return true;
    }

    private setColumns = (): void => {
        const columns: ISlickGridColumn<Action>[] = [
            { id: 'actionSeason', field: 'actionSeason', name: 'ActionSeason', sortable: true },
            { id: 'status', field: 'status', name: 'Status', sortable: true }
        ];
        if (!this.isFresh)
            columns.push({ id: 'type', field: 'type', name: 'Type', sortable: true });
        columns.push({ id: 'purchaseDate', field: 'purchaseDate', name: 'PurchaseDate', sortable: true/*, formatter: (r, c, v) => Formatters.dateFormatter(r, c, v)*/ });
        columns.push({ id: 'startDate', field: 'startDate', name: 'StartDate', sortable: true/*, formatter: (r, c, v) => Formatters.dateFormatter(r, c, v)*/ });
        columns.push({ id: 'endDate', field: 'endDate', name: 'EndDate', sortable: true/*, formatter: (r, c, v) => Formatters.dateFormatter(r, c, v)*/ });
        columns.push({ id: 'saleChangeRatio', field: 'saleChangeRatio', name: 'SaleChangeRatio', sortable: true/*, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 2)*/ });
        columns.push({ id: 'saleIncrease', field: 'saleIncrease', name: 'SaleIncreaseQnt', sortable: true/*, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 2)*/ });
        if (!this.isFresh) {
            columns.push({ id: 'safetyStock', field: 'safetyStock', name: 'SafetyStock', sortable: true/*, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 2)*/ });
            columns.push({ id: 'averageSaleOverReplTime', field: 'averageSaleOverReplTime', name: 'AverageSaleOverReplTime', sortable: true/*, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 2)*/ });
            columns.push({ id: 'bufferOld', field: 'bufferOld', name: 'BufferOld', sortable: true/*, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 4)*/ });
            columns.push({ id: 'bufferNew', field: 'bufferNew', name: 'BufferNew', sortable: true/*, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 4)*/ });
            columns.push({ id: 'bufferAfterAction', field: 'bufferAfterAction', name: 'BufferAfterAction', sortable: true/*, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 4)*/ });
        }
        columns.push({ id: 'replenishment', field: 'replenishment', name: 'Replenishment', sortable: true });
        columns.push({ id: 'actionOut', field: 'actionOut', name: 'ActionOut', sortable: true });
        if (!this.isFresh)
            columns.push({ id: 'nestedAction', field: 'nestedAction', name: 'NestedAction', sortable: true });
        columns.push({ id: 'importDate', field: 'importDate', name: 'ImportDate', sortable: true/*, formatter: (r, c, v) => Formatters.dateFormatter(r, c, v)*/ });
        columns.push({ id: 'documentNumber', field: 'documentNumber', name: 'DocumentNumber', sortable: true });

        this.columns = columns;

        if (this.isFresh)
            this.freshLineColumns = [
                { id: 'orderDate', field: 'orderDate', name: 'OrderDate', sortable: true/*, formatter: (r, c, v) => Formatters.dateFormatter(r, c, v)*/ },
                { id: 'arrivalDate', field: 'arrivalDate', name: 'ArrivalDate', sortable: true, formatter: (r, c, v) => Formatters.datetimeFormatter(r, c, v) },
                { id: 'nextArrivalDate', field: 'nextArrivalDate', name: 'NextArrivalDate', sortable: true/*, formatter: (r, c, v) => Formatters.dateFormatter(r, c, v)*/ },
                { id: 'buffer', field: 'buffer', name: 'Buffer', sortable: true, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 4) },
                { id: 'actionBuffer', field: 'actionBuffer', name: 'IncludingActionBuffer', sortable: true, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 4) },
                { id: 'balance', field: 'balance', name: 'Balance', sortable: true, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 2) },
                { id: 'inTransit', field: 'inTransit', name: 'InTransit', sortable: true, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 2) },
                { id: 'order', field: 'order', name: 'Order', sortable: true, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 2) },
                { id: 'actionOrder', field: 'actionOrder', name: 'IncludingActionOrder', sortable: true, formatter: (r, c, v) => Formatters.numberFormatter(r, c, v, 2) }
            ];
    }

    private onMainGridActiveCellChanged = (args: EventArgs<Slick.OnActiveCellChangedEventArgs<any>>): void => {
        let action: FreshItemActionHeader;
        if (!this.isFresh || (action = this.dataView.getItemByIdx(args.data.row) as FreshItemActionHeader) === this.selectedFreshHeader)
            return;

        this.freshLineDataView.setItems(action.lines);
        this.selectedFreshHeader = action;
    }

    private onShowCurrentClick = (): void => {
        if (!this.allShown)
            return;

        this.showCurrentActions.emit();
    }

    private onShowAllClick = (): void => {
        if (this.allShown)
            return;

        this.showAllActions.emit();
    }

    private onDownloadClick = (): void => {
        if (this.isFresh)
            return;
        const fileName = `Actions_SKU_${this.sku}_Wrh_${this.warehouseCode}.xlsx`;
        this.translateService.get('Actions').subscribe(t => {
            this.download(data => this.graphService.downloadItemActions(data.columns, data.rows, moment.locale(), t)
                .map(file => { return { fileContent: file, fileName }; }));
        });
    }

    private onSendClick = (): void => {
        if (this.isFresh)
            return;
        this.translateService.get('Actions').subscribe(t => {
            this.sendToEmail(data => this.graphService.sendItemActions(this.sku, this.warehouseCode, data.columns, data.rows, moment.locale(), t));
        });
    }

    //private onHideExportErrorClick = (): void => {
    //    setTimeout(() => this.showExportError = false, 500);
    //}

    //private onHideSendErrorClick = (): void => {
    //    setTimeout(() => this.showSendError = false, 500);
    //}

    //private onHideSendSuccessClick = (): void => {
    //    setTimeout(() => this.showSendSuccess = false, 500);
    //}

    private onGridsResizedBy = (size: Size): void => {
        if (this.resizeTimer)
            clearTimeout(this.resizeTimer);

        this.resizeValue += size.height;

        this.resizeTimer = setTimeout(() => {
            this.resizeTimer = null;

            const headersHeight = this.headersRef.nativeElement.offsetWidth;
            const linesHeight = this.linesRef.nativeElement.offsetWidth;

            let heightCoeff = (headersHeight - this.resizeValue) / (headersHeight + linesHeight);
            if (heightCoeff < 0)
                heightCoeff = 0;
            else if (heightCoeff > 1)
                heightCoeff = 1;

            localStorage['IaHeightCoeff'] = heightCoeff;
            this.freshTemplateRows = `${heightCoeff}fr min-content ${1 - heightCoeff}fr min-content`;

            this.onTabsResized();

            this.resizeValue = 0;
        },
            100);
    }
}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/components/item-actions.component/item-actions.component.ts